import React, { useEffect, useMemo, useState } from "react";
import { hasPermission, hasRole, showToast } from "../../utils/utils";
import { useSelector } from "react-redux";
import { selectUserInformations } from "../../features/authSlice";
import { useNavigate } from "react-router-dom";
import Loader from "../Live/Loader";
import CourseGradingStatus from "../Exams/CourseGradingStatus";
import { FiEdit, FiRefreshCw } from "react-icons/fi";
import { MdDeleteOutline } from "react-icons/md";
import ActionsDropdown from "../reusable/ActionsDropdown";
import Pagination from "../reusable/Pagination";
import Header from "../Lesson/Header";
import LiveTypeList from "../Live/LiveTypeList";
import InstrctorIcon from "../../assets/svg/InstrctorIcon.svg";
import Play from "../../assets/gif/play.gif";
import BlueDot from "../../assets/svg/BlueDot.svg";
import GrayDot from "../../assets/svg/GrayDot.svg";
import RocketLive from "../../assets/gif/rocket-live.gif";
import { API } from "../../shared/api/Api";
import EntityService from "../../services/EntityService";
import Modal from "../Modal";
import Info from "../../assets/gif/LiveInfo.gif";
import Close from "../../assets/svg/CloseButton.svg";
import Tooltip from "../reusable/Tooltip";
import { FaSortDown, FaSortUp } from "react-icons/fa";

const LessonTable = ({
  lessons,
  currentPage,
  totalPages,
  onSearchChange,
  onPageChange,
  loader,
  setReFetchData,
  course,
  onSort,
  currentOrderBy,
  currentOrderDir,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [liveState, setLiveState] = useState("");
  const [liveDetails, setLiveDetails] = useState(null);
  const [examDetails, setExamDetails] = useState(null);
  const user = useSelector(selectUserInformations);
  const navigate = useNavigate();
  const NEXT_LIVE_APP = process.env.REACT_APP_FRONTEND_STREAM_URL ?? 'http://localhost:3001';

  const isStudent = useMemo(() => user.isStudent, [user]);

  const studentPayment = useMemo(() => {
    if (!isStudent) return null;
    if (!course || !Array.isArray(course?.paymentStudent) || course.paymentStudent.length === 0) {
      return null;
    }

    return (
      course.paymentStudent.find((payment) => {
        const studentId =
          typeof payment?.student_id === "string"
            ? Number(payment.student_id)
            : payment?.student_id;
        const currentUserId =
          typeof user?.student?.id === "string"
            ? Number(user?.student?.id)
            : user?.student?.id;
        return studentId === currentUserId;
      }) || null
    );
  }, [course, isStudent, user?.student?.id]);

  // Quick stats for header
  const lessonStats = useMemo(() => {
    const total = lessons?.length || 0;
    const active = lessons?.filter((l) => l.status === "active").length || 0;
    const withLive =
      lessons?.filter(
        (l) => l.liveSession !== null && l.liveSession !== undefined
      ).length || 0;
    const totalDuration =
      lessons?.reduce(
        (sum, l) =>
          sum +
          (typeof l.duration_in_minutes === "number"
            ? l.duration_in_minutes
            : 0),
        0
      ) || 0;

    return {
      total,
      active,
      withLive,
      totalDuration,
    };
  }, [lessons]);

  const handleNavigateToPurchase = () => {
    if (!course?.id) return;
    navigate(`/dashboard/courses/${course.id}/purchase`);
  };

  // Delete lesson function
  const deleteEntity = async (token, lessonId) => {
    try {
      const response = await EntityService.deleteEntity(
        token,
        API.Courses.app_courses_lessons_delete,
        lessonId
      );
      if (response.data.status === 200) {
        return { success: true };
      }
      throw new Error(response.data.message || 'Failed to delete lesson');
    } catch (error) {
      throw error;
    }
  };


  // Handle opening special modals (live details, exam details)
  const handleOpenModal = (type, id = null) => {
    setActionType(type);
    setIsModalOpen(true);
  };

  const renderModalContent = () => {
    switch (actionType) {
      case "details":
        return <LiveDetails details={liveDetails} onClose={handleCloseModal} />;
      case "exam":
        return <ExamDetails exam={examDetails} onClose={handleCloseModal} />;
      default:
        return null;
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setActionType(null);
    setLiveDetails(null);
    setExamDetails(null);
  };

  const getDropdownActions = (id) => {
    const actions = [];

    if (hasPermission(user, "view_lesson")) {
      actions.push({
        icon: (
          <svg
            className="w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ),
        label: "Preview",
        onClick: () => navigate(`/dashboard/courses/${course?.id}/lessons/${id}`, { state: { course } }),
      });
    }

    if (hasPermission(user, "update_lesson")) {
      actions.push({
        icon: <FiEdit className="w-4 h-4" />,
        label: "Edit",
        onClick: () => navigate(`/dashboard/courses/${course?.id}/lessons/${id}/edit`, { state: { course } }),
      });
    }

    if (hasPermission(user, "delete_lesson")) {
      actions.push({
        icon: <MdDeleteOutline className="w-4 h-4" />,
        label: "Delete",
        onClick: async () => {
          if (window.confirm("Are you sure you want to delete this lesson? There is no going back!!")) {
            try {
              await deleteEntity(user.token, id);
              showToast('Success', 'Lesson deleted successfully', 'success');
              if (setReFetchData) {
                setReFetchData(true);
              }
            } catch (error) {
              showToast('Error', error.message || 'Failed to delete lesson', 'error');
            }
          }
        },
      });
    }

    return actions;
  };



  const columns = [
    { id: "title", label: "Title", sortable: true },
    { id: "description", label: "Description", sortable: true },
    { id: "duration_in_minutes", label: "Duration", sortable: true },
    { id: "created_at", label: "Created at", sortable: true },
    { id: "replay", label: "Replay", sortable: false },
    { id: "live", label: "Start Live", sortable: false },
    { id: "details", label: "Live Details", sortable: false },
    { id: "exam_details", label: "Exam Details", sortable: false },
    { id: "actions", label: "Actions", sortable: false },
  ];

  // Handle sorting when a column header is clicked
  const handleSortClick = (columnId) => {
    if (currentOrderBy === columnId) {
      const newDirection = currentOrderDir === "ASC" ? "DESC" : "ASC";
      onSort(columnId, newDirection);
    } else {
      onSort(columnId, "ASC");
    }
  };

  const handleNavigationToLive = (lesson_id) => {
    window.open(`${NEXT_LIVE_APP}/redirect-to-live?lesson=${lesson_id}`, "_blank", "noopener,noreferrer");
  }

  if (isStudent && studentPayment === null) {
    return (
      <div className="w-full max-w-full mx-auto mt-10 p-6 sm:p-10 md:p-12 bg-white shadow-lg rounded-xl text-center space-y-6">
        <h2 className="text-2xl font-semibold text-primary">Purchase Required</h2>
        <p className="text-gray-600">You need to purchase this course to access its lessons.</p>
        <button
          type="button"
          onClick={handleNavigateToPurchase}
          className="inline-flex items-center justify-center px-6 py-3 text-white bg-primary rounded-lg hover:bg-primary-hover transition"
        >
          Buy Course
        </button>
      </div>
    );
  }

  if (isStudent && studentPayment && studentPayment.status !== "verified") {
    return (
      <div className="w-full max-w-full mx-auto mt-10 p-6 sm:p-10 md:p-12 bg-white shadow-lg rounded-xl text-center space-y-6">
        <h2 className="text-2xl font-semibold text-primary">Payment Pending</h2>
        <p className="text-gray-600">Your payment is under review. Please wait for an administrator to activate the course.</p>
      </div>
    );
  }

  // Render sort indicators
  const renderSortIndicator = (columnId) => {
    const isActive = currentOrderBy === columnId;

    return (
      <div className="inline-flex flex-col ml-1">
        <FaSortUp
          className={`${isActive && currentOrderDir === "ASC"
            ? "text-primary"
            : "text-gray-400"
            } -mb-1`}
          size={12}
        />
        <FaSortDown
          className={`${isActive && currentOrderDir === "DESC"
            ? "text-primary"
            : "text-gray-400"
            } -mt-1`}
          size={12}
        />
      </div>
    );
  };

  return (
    <div className="w-full mx-auto mt-8 px-2 sm:px-4 lg:px-0">
      <div className="bg-white border border-gray-100 shadow-xl rounded-2xl p-4 sm:p-6 lg:px-8 lg:py-6 relative">
        <>
          <LiveTypeList
            lesson={selectedLesson}
            liveState={liveState}
            setLiveState={setLiveState}
          />
        </>

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="space-y-3">
            <div>
              <h1 className="font-anek-odia text-2xl sm:text-3xl font-semibold text-gray-900">
                <Header course={course} />
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage lessons, live sessions, and exam details for this course.
              </p>
            </div>

            {/* Quick stats */}
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                  {lessonStats.total}
                </span>
                <span className="text-xs font-medium text-blue-800">
                  Total Lessons
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                  {lessonStats.active}
                </span>
                <span className="text-xs font-medium text-emerald-800">
                  Active Lessons
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-purple-100 text-purple-700 text-xs font-semibold">
                  {lessonStats.withLive}
                </span>
                <span className="text-xs font-medium text-purple-800">
                  With Live Session
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-gray-800 text-xs font-semibold">
                  {lessonStats.totalDuration}
                </span>
                <span className="text-xs font-medium text-gray-700">
                  Total Minutes
                </span>
              </div>
            </div>
          </div>

          {/* Add Lesson Button and Refresh Button */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {hasPermission(user, "create_lesson") && (
              <button
                type="button"
                onClick={() =>
                  navigate(`/dashboard/courses/${course?.id}/lessons/create`, {
                    state: { course },
                  })
                }
                className="inline-flex items-center justify-center gap-2 text-white bg-primary rounded-full px-3 sm:px-4 h-10 sm:h-11 text-xs sm:text-sm font-medium hover:bg-primary-hover transition-colors"
                title="Add Lesson"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                  <svg
                    className="w-3.5 h-3.5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 18 18"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 1v16M1 9h16"
                    />
                  </svg>
                </span>
                <span className="hidden sm:inline">New Lesson</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setReFetchData(true)}
              className="inline-flex items-center justify-center gap-2 text-gray-700 bg-white border border-gray-200 rounded-full px-3 h-10 sm:h-11 text-xs sm:text-sm font-medium hover:bg-gray-50 transition-colors"
              title="Refresh Data"
            >
              <FiRefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Instructor and Search Section */}
        <div className="mt-5 flex flex-col sm:flex-row justify-between items-start sm:items-center border-top border-gray-100 pt-2 gap-3">
          <div className="flex items-center gap-2">
            <img
              className="w-6 h-6 sm:w-8 sm:h-8"
              src={InstrctorIcon}
              alt="instrctor-icon"
            />
            <span className="text-sm font-medium text-gray-600">
              Instructor:
            </span>
            <span className="text-sm font-semibold text-gray-900">
              {course?.instructor_name || "-"}
            </span>
          </div>
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              placeholder="Search lessons..."
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full rounded-full border border-gray-200 bg-gray-50 px-10 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 outline-none focus:border-primary focus:bg-white focus:ring-1 focus:ring-primary transition"
            />
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-4.35-4.35M11 5a6 6 0 100 12 6 6 0 000-12z"
                />
              </svg>
            </span>
          </div>
        </div>

      {/* Table Section */}
      <div className="mt-6 rounded-2xl border border-gray-100 bg-white overflow-hidden">
        <div className="w-full overflow-x-auto">
          <div className="min-w-[960px]">
        {loader && (
          <div className="flex justify-center items-center py-6">
            <Loader />
          </div>
        )}
        <table className="w-full table-auto">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  className={`px-4 sm:px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide ${
                    currentOrderBy === column.id ? "text-primary" : "text-gray-500"
                  } border-b border-gray-100 ${
                    column.sortable ? "cursor-pointer hover:bg-gray-100" : ""
                  }`}
                  onClick={
                    column.sortable ? () => handleSortClick(column.id) : undefined
                  }
                >
                  <div className="flex items-center gap-1">
                    {column.label}
                    {column.sortable && renderSortIndicator(column.id)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {lessons?.map((lesson, index) => (
              <React.Fragment key={index}>
                <tr className="hover:bg-gray-50 transition-colors cursor-auto text-sm">
                  <td className="px-4 sm:px-6 py-4">
                    <div className="flex flex-row items-center space-x-2 sm:space-x-3">
                      <img
                        className="w-[18px] h-[18px] sm:w-[23px] sm:h-[23px]"
                        src={lesson.status === "active" ? BlueDot : GrayDot}
                        alt="Status Dot"
                      />
                      <span className="font-medium text-gray-900">
                        {lesson.title || "N/A"}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-gray-700">
                    {lesson.description || "N/A"}
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-gray-700">
                    {lesson.duration_in_minutes || "N/A"}
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-gray-700">
                    {lesson.created_at || "N/A"}
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    {lesson?.liveSession?.record ? (
                      <a
                        href={lesson?.liveSession?.record.recording_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          className="cursor-pointer h-6 w-6 sm:h-8 sm:w-8"
                          src={Play}
                          alt="play"
                        />
                      </a>
                    ) : (
                      <div>
                        <Tooltip
                          content="Record not ready, it may took 5 min"
                          key={lesson?.id}
                        >
                          <img
                            className="cursor-not-allowed h-6 w-6 sm:h-8 sm:w-8"
                            src={Play}
                            alt="play"
                          />
                        </Tooltip>
                      </div>
                    )}
                  </td>
                  {(user.isInstructor ||
                    hasRole(user.roles, "super-admin")) && (
                      <>
                        <td className="px-4 sm:px-6 py-4">
                          {lesson.status === "active" &&
                            lesson.liveSession === null ? (
                            <img
                              onClick={() => handleNavigationToLive(lesson.id)}
                              className="cursor-pointer h-6 w-6 sm:h-8 sm:w-8"
                              src={RocketLive}
                              alt="start-live"
                            />
                          ) : (
                            <span className="text-gray-400"></span>
                          )}
                        </td>
                      </>
                    )}
                  {(hasPermission(user, "view_lesson") ||
                    hasPermission(user, "create_lesson") ||
                    hasPermission(user, "update_lesson") ||
                    hasPermission(user, "delete_lesson")) && (
                      <td className="px-4 sm:px-6 py-4 text-center">
                        {lesson.liveSession ? (
                          <img
                            className="cursor-pointer h-6 w-6 sm:h-8 sm:w-8"
                            src={Info}
                            alt="live-details"
                            onClick={() => {
                              setLiveDetails(lesson);
                              handleOpenModal("details");
                            }}
                          />
                        ) : (
                          <span className="text-gray-400"></span>
                        )}
                      </td>
                    )}
                  {/* Exam Details */}
                  <td className="px-4 sm:px-6 py-4 text-center">
                    {lesson.has_exam && lesson.exams ? (
                      <button
                        type="button"
                        className="px-3 py-1 text-xs md:text-sm rounded-md border border-primary text-primary hover:bg-primary hover:text-white transition-colors"
                        onClick={() => {
                          setExamDetails(lesson.exams);
                          handleOpenModal("exam");
                        }}
                      >
                        Exam Details
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs">No Exam</span>
                    )}
                  </td>
                  {/* Actions (Conditional) */}
                  {(hasPermission(user, "view_lesson") ||
                    hasPermission(user, "update_lesson") ||
                    hasPermission(user, "delete_lesson")) && (
                      <td className="px-4 sm:px-6 py-4 text-right">
                        <ActionsDropdown
                          actions={getDropdownActions(lesson.id)}
                        />
                      </td>
                    )}
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4">
        <p className="hidden sm:block text-xs text-gray-500">
          Showing page <span className="font-semibold">{currentPage}</span> of{" "}
          <span className="font-semibold">{totalPages}</span>
        </p>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>

      {/* Course Completion Section - Super Admin Only */}
      {hasRole(user?.roles, 'super-admin') && course?.id && (
        <div className="mt-8 pt-6 border-t-2 border-gray-200">
          <CourseGradingStatus 
            courseId={course.id} 
            courseName={course.title}
          />
        </div>
      )}

      {/* Special Modals (Live Details, Exam Details) */}
      <Modal
        isOpen={isModalOpen && (actionType === "details" || actionType === "exam")}
        onClose={handleCloseModal}
        width={actionType === "details" ? "max-w-7xl" : "max-w-5xl"}
        withHeader={false}
        withButtons={false}
      >
        {renderModalContent()}
      </Modal>
      </div>
    </div>
  );
};

export default LessonTable;

const LiveDetails = ({ details, onClose }) => {
  const liveSession = details?.liveSession || {};
  const room = details?.room || {};

  return (
    <div className="relative p-4 w-full max-w-[90vw] md:max-w-3xl lg:max-w-7xl mx-auto overflow-y-auto">
      <div className="flex justify-between items-center pb-4 mb-4 border-b border-gray-200">
        <h3 className="text-xl md:text-2xl font-semibold text-gray-900 capitalize">
          Live Details For {details?.title}
        </h3>
        <button
          onClick={onClose}
          className="p-2 -mr-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
        >
          <img className="w-6 h-6 md:w-8 md:h-8" src={Close} alt="Close" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Left Column - Session Details */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="text-lg font-semibold text-blue-700">
              Session Information
            </h4>
            <div className="flex items-center">
              <span
                className={`w-3 h-3 rounded-full mr-2 ${details?.status === "active" ? "bg-blue-500" : "bg-gray-400"
                  }`}
              ></span>
              <span className="text-sm capitalize">{details?.status}</span>
            </div>
          </div>

          <DetailItem label="Lesson Title" value={details?.title} />
          <DetailItem label="Lesson Description" value={details?.description} />
          <DetailItem
            label="Lesson Duration"
            value={`${details?.duration_in_minutes} minutes`}
          />
          <DetailItem
            label="Lesson Instructor"
            value={details?.instrctor_name}
          />
          <DetailItem
            label="Started At"
            value={
              liveSession.ended_at
                ? new Date(liveSession.started_at).toLocaleDateString("en-GB") +
                ", " +
                new Date(liveSession.started_at).toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "2-digit",
                  second: "2-digit",
                })
                : ""
            }
          />
          <DetailItem
            label="Ended At"
            value={
              liveSession.ended_at
                ? new Date(liveSession.ended_at).toLocaleDateString("en-GB") +
                ", " +
                new Date(liveSession.ended_at).toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "2-digit",
                  second: "2-digit",
                })
                : ""
            }
          />
        </div>

        {/* Right Column - Room Details */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-blue-700">
            Room Information
          </h4>
          <DetailItem label="Room ID" value={room?.id} />
          <DetailItem label="Room Name" value={room?.room_name} />
          <DetailItem label="UUID" value={room?.uuid} />
          <DetailItem
            label="Chat Enabled"
            value={room?.chat_enabled ? "Yes" : "No"}
            valueClass={room?.chat_enabled ? "text-green-600" : "text-red-600"}
          />
          <DetailItem label="Created At" value={room?.created_at} />
          <DetailItem label="Last Updated" value={room?.updated_at} />
        </div>
      </div>

      {/* Session Notes */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="text-sm font-semibold text-blue-800 mb-2">
          Session Notes
        </h4>
        <p className="text-gray-700 text-sm whitespace-pre-wrap">
          {liveSession?.notes || "No notes available for this session"}
        </p>
      </div>
    </div>
  );
};

// Reusable detail item component
const DetailItem = ({ label, value, valueClass = "" }) => (
  <div className="flex justify-between items-start py-2 border-b border-gray-100">
    <span className="text-sm font-medium text-gray-600">{label}:</span>
    <span className={`text-sm text-gray-800 text-right ${valueClass}`}>
      {value || "-"}
    </span>
  </div>
);

const ExamDetails = ({ exam, onClose }) => {
  if (!exam) return null;
  return (
    <div className="relative p-4 w-full max-w-[90vw] md:max-w-3xl lg:max-w-5xl mx-auto overflow-y-auto">
      <div className="flex justify-between items-center pb-4 mb-4 border-b border-gray-200">
        <h3 className="text-xl md:text-2xl font-semibold text-gray-900">
          Exam Details
        </h3>
        <button
          onClick={onClose}
          className="p-2 -mr-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
        >
          <img className="w-6 h-6 md:w-8 md:h-8" src={Close} alt="Close" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <DetailItem label="Title" value={exam.title} />
          <DetailItem label="Description" value={exam.description} />
          <DetailItem label="Duration (minutes)" value={exam.duration_minutes} />
          <DetailItem label="Passing Score" value={exam.passing_score} />
          <DetailItem label="Randomized Questions" value={exam.is_randomized ? "Yes" : "No"} valueClass={exam.is_randomized ? "text-green-600" : "text-red-600"} />
          <DetailItem label="Allow Multiple Attempts" value={exam.allow_multiple_attempts ? "Yes" : "No"} valueClass={exam.allow_multiple_attempts ? "text-green-600" : "text-red-600"} />
          <DetailItem label="Max Attempts" value={exam.max_attempts} />
        </div>
        <div className="space-y-2">
          <DetailItem label="Show Answers After" value={exam.show_answers_after} />
          <DetailItem label="Status" value={exam.status} />
          <DetailItem label="Exam Code" value={exam.exam_code} />
          <DetailItem label="Started" value={exam.started ? "Yes" : "No"} valueClass={exam.started ? "text-green-600" : "text-red-600"} />
          <DetailItem label="Created At" value={exam.created_at} />
          <DetailItem label="Updated At" value={exam.updated_at} />
        </div>
      </div>
    </div>
  );
};
